import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Users, CheckCircle, Clock, BookOpen, FileText } from 'lucide-react';

const EvaluationTable = ({
  controles,
  handleEdit,
  handleDelete,
  formatDate,
  formatTime
}) => {
  const navigate = useNavigate();

  const handleManageNotes = (controle) => {
    navigate(`/evaluations/notes/${controle.id}`);
  };

  const handleViewNotes = (controle) => {
    navigate(`/evaluations/liste-notes/${controle.id}`);
  };
  const getLibelleColor = (libelle) => {
    switch (libelle.toLowerCase()) {
      case 'devoir':
        return 'bg-blue-100 text-blue-800';
      case 'interrogation':
        return 'bg-orange-100 text-orange-800';
      case 'expose':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    return status 
      ? 'bg-green-100 text-green-800' 
      : 'bg-yellow-100 text-yellow-800';
  };

  const getStatusIcon = (status) => {
    return status 
      ? <CheckCircle className="h-4 w-4" />
      : <Clock className="h-4 w-4" />;
  };

  const getMoyenneColor = (moyenne) => {
    if (moyenne >= 16) return 'text-green-600 font-semibold';
    if (moyenne >= 12) return 'text-blue-600 font-semibold';
    if (moyenne >= 10) return 'text-orange-600 font-semibold';
    return 'text-red-600 font-semibold';
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Évaluation
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Classe & Module
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date & Heure
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Statut
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Statistiques
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {controles.map((controle, index) => (
            <tr key={controle.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              {/* Évaluation */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-lg bg-[#202654]/10 flex items-center justify-center">
                      <BookOpen className="h-5 w-5 text-[#202654]" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLibelleColor(controle.libelle)}`}>
                        {controle.libelle}
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        N° {controle.numero}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {controle.semestre?.libelle || 'Semestre non défini'}
                    </div>
                  </div>
                </div>
              </td>

              {/* Classe & Module */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {controle.classe?.libelle || 'Classe non définie'}
                </div>
                <div className="text-sm text-gray-500">
                  {controle.module?.module?.libelle || 'Module non défini'}
                </div>
              </td>

              {/* Date & Heure */}
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {formatDate(controle.date_controle)}
                </div>
                <div className="text-sm text-gray-500">
                  {formatTime(controle.heure_debut)} - {formatTime(controle.heure_fin)}
                </div>
              </td>

              {/* Statut */}
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(controle.status_note)}`}>
                  {getStatusIcon(controle.status_note)}
                  <span className="ml-1">
                    {controle.status_note ? 'Noté' : 'En attente'}
                  </span>
                </span>
              </td>

              {/* Statistiques */}
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className="flex flex-col items-center space-y-1">
                  <div className="text-sm text-gray-500">
                    {controle.notes_count || 0} note(s)
                  </div>
                  {controle.status_note && (
                    <div className={`text-sm ${getMoyenneColor(controle.moyenne || 0)}`}>
                      Moy: {controle.moyenne || 0}/20
                    </div>
                  )}
                  {controle.status_note && (
                    <div className="text-xs text-gray-400">
                      Min: {controle.note_min || 0} | Max: {controle.note_max || 0}
                    </div>
                  )}
                </div>
              </td>

              {/* Actions */}
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="flex items-center justify-center space-x-2">
                  <button
                    onClick={() => handleManageNotes(controle)}
                    className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                    title="Gérer les notes"
                  >
                    <Users className="h-4 w-4" />
                  </button>
                  {controle.status_note && (
                    <button
                      onClick={() => handleViewNotes(controle)}
                      className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                      title="Voir la liste des notes"
                    >
                      <FileText className="h-4 w-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(controle)}
                    className="text-indigo-600 hover:text-indigo-900 p-1 rounded hover:bg-indigo-50"
                    title="Modifier"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(controle)}
                    className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default EvaluationTable; 